import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import { Card, Link as MuiLink } from '@material-ui/core'
import { Box } from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { toast } from 'utils/notify'
import AppLogo from '../../assets/primary_logo.png'
import { colors } from 'utils/colors'
import { Link } from 'react-router-dom'
import ProgressButton from 'components/ProgressButton'
import firebase from 'firebase'

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required')
})

const ForgotPassword = () => {
  const classes = useStyles()
  const [emailSent, setEmailSent] = useState(false)
  const [performingAction, setPerformingAction] = useState(false)

  const onSubmit = async (values) => {
    try {
      setPerformingAction(true)
      await firebase.auth().sendPasswordResetEmail(values.email, {
        url: window.location.origin + '/reset-password',
        handleCodeInApp: false
      })
      setEmailSent(true)
    } catch (error) {
      console.error(error)
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email address.')
      } else {
        toast.error('Could not send reset email: ' + error.message)
      }
    } finally {
      setPerformingAction(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: onSubmit
  })

  return (
    <div className={classes.container}>
      <div className={classes.overlay}></div>
      <Card className={classes.card} elevation={5}>
        <div className={classes.logoContainer}>
          <img src={AppLogo} alt="MeetSymple Logo" className={classes.logo} />
        </div>
        <Typography className={classes.header} variant="h5">
          Forgot Password
        </Typography>

        {emailSent ? (
          <Box mb={2}>
            <Typography variant="body1" align="center" gutterBottom>
              A password reset link has been sent to your email.
            </Typography>
            <Typography variant="body2" align="center">
              Please check your inbox and follow the instructions to reset your password.
            </Typography>
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit} className={classes.form}>
            <Typography variant="body2" align="center" gutterBottom>
              Enter your email address and we&apos;ll send you a link to reset your password.
            </Typography>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email"
              variant="outlined"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
            />

            <ProgressButton
              fullWidth
              type="submit"
              className={classes.submitButton}
              label="Send Reset Link"
              inProgress={performingAction}
              disabled={performingAction || !formik.isValid}
            />
          </form>
        )}

        <Box mt={2} textAlign="center">
          <Typography variant="body2">
            <MuiLink
              component={Link}
              to="/"
              color="primary"
              style={performingAction ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
              Back to Login
            </MuiLink>
          </Typography>
        </Box>
      </Card>
    </div>
  )
}

const useStyles = makeStyles({
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      transition: 'all 0.3s',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#15b4db'
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
      borderWidth: '1px'
    }
  },
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '20px'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    zIndex: 1
  },
  card: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '450px',
    borderRadius: '16px',
    padding: '35px 40px',
    boxShadow: '0 15px 25px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.95)',
    animation: '$fadeIn 0.8s ease-out',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  logo: {
    height: '60px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2))'
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '24px',
    color: colors.indigo
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '16px'
  },
  submitButton: {
    width: '100%',
    marginTop: '24px',
    padding: '12px',
    height: '48px',
    borderRadius: '8px',
    fontWeight: '600',
    letterSpacing: '0.5px'
  }
})

export default ForgotPassword
